import React, { useState } from "react"
import { Tabs, Input } from "antd"
import ConfirmationModal from "shared/ui/ConfimationModal/ConfimationModal.component"
import type { Page } from "features/Dashboard/types/dashboardDocument"
import { useDashboardStore } from "features/Dashboard/runtime/dashboardStore"
import TilePalette from "./TilePalette.component"

/** MIME type of a page tab drag; the value is the page id. */
const DRAG_PAGE_TYPE = "application/x-dataq-page"

interface PageTabsProps {
  readOnly?: boolean
}

/**
 * The page strip. Build mode: add, close (after a confirmation), rename by
 * double-clicking a label, reorder by dragging a label onto another, and the
 * tile palette on the right. View mode: plain tabs.
 */
const PageTabs: React.FC<PageTabsProps> = ({ readOnly = false }) => {
  const pages = useDashboardStore((s) => s.document.pages)
  const activePageId = useDashboardStore((s) => s.activePageId)
  const setActivePage = useDashboardStore((s) => s.setActivePage)
  const addPage = useDashboardStore((s) => s.addPage)
  const removePage = useDashboardStore((s) => s.removePage)
  const renamePage = useDashboardStore((s) => s.renamePage)
  const movePage = useDashboardStore((s) => s.movePage)
  const [editing, setEditing] = useState<{ id: string; title: string } | null>(
    null
  )
  const [pendingRemove, setPendingRemove] = useState<string | null>(null)

  const commitRename = (p: Page): void => {
    if (!editing) return
    const title = editing.title.trim() || p.title
    if (title !== p.title) renamePage(p.id, title)
    setEditing(null)
  }

  const label = (p: Page): React.ReactNode =>
    editing?.id === p.id && !readOnly ? (
      <Input
        size="small"
        autoFocus
        className="dq-page-tab__input"
        value={editing.title}
        aria-label="Page title"
        onChange={(e) => setEditing({ id: p.id, title: e.target.value })}
        // Focus and keys stay in the field: on focus the tab strip moves focus
        // to the tab itself (closing the field), and it takes Space, the
        // arrows and Delete for its own navigation.
        onFocus={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onPressEnter={() => commitRename(p)}
        onBlur={() => commitRename(p)}
      />
    ) : (
      <span
        className="dq-page-tab__label"
        draggable={!readOnly}
        onDoubleClick={() => !readOnly && setEditing({ id: p.id, title: p.title })}
        onDragStart={(e) => {
          e.dataTransfer.setData(DRAG_PAGE_TYPE, p.id)
          e.dataTransfer.effectAllowed = "move"
        }}
        // A drop target has to accept the dragover first.
        onDragOver={(e) => {
          if (!readOnly && e.dataTransfer.types.includes(DRAG_PAGE_TYPE)) {
            e.preventDefault()
          }
        }}
        onDrop={(e) => {
          const draggedId = e.dataTransfer.getData(DRAG_PAGE_TYPE)
          if (readOnly || !draggedId || draggedId === p.id) return
          e.preventDefault()
          movePage(
            draggedId,
            pages.findIndex((x) => x.id === p.id)
          )
        }}
      >
        {p.title}
      </span>
    )

  return (
    <>
      <Tabs
        size="small"
        type={readOnly ? "line" : "editable-card"}
        hideAdd={readOnly}
        activeKey={activePageId}
        onChange={setActivePage}
        className={`dq-page-tabs ${readOnly ? "dq-page-tabs--view" : "dq-page-tabs--build"}`}
        onEdit={(key, action) => {
          if (action === "add") addPage()
          else if (typeof key === "string") setPendingRemove(key)
        }}
        items={pages.map((p) => ({
          key: p.id,
          label: label(p),
          closable: !readOnly && pages.length > 1,
        }))}
        tabBarExtraContent={readOnly ? undefined : <TilePalette />}
      />
      <ConfirmationModal
        title="Delete page"
        isVisible={pendingRemove !== null}
        message="Delete this page and its tiles?"
        confirmButtonText="Delete"
        confirmButtonType="danger"
        cancel={() => setPendingRemove(null)}
        confirm={() => {
          if (pendingRemove) removePage(pendingRemove)
          setPendingRemove(null)
        }}
      />
    </>
  )
}

export default PageTabs
